import { Component, Input } from '@angular/core';

/**
 * Live client-side feedback for any "set a new password" form, mirroring the backend's
 * complexity rule exactly (CRM.Services.PasswordPolicy.ValidateComplexity: 8+ chars, 3-of-4
 * character classes) so the user sees what's still missing as they type, instead of only
 * finding out via a submit/reject round trip. The common-password blocklist and
 * password-history reuse checks are server-only (no point duplicating a several-hundred-entry
 * list client-side, and history can't be checked without hitting the server anyway) - those
 * two still only surface after a real submit attempt.
 *
 * Used from every screen that can set a new password: the forced password-change step on
 * login, and the profile page's self-serve change-password form.
 */
@Component({
  selector: 'password-requirements-checklist',
  exportAs: 'passwordRequirementsChecklist',
  template: `
    <ul class="password-requirements-checklist">
      <li [class.met]="hasMinLength"><i class="fa" [ngClass]="hasMinLength ? 'fa-check' : 'fa-times'"></i> At least 8 characters</li>
      <li [class.met]="hasEnoughClasses"><i class="fa" [ngClass]="hasEnoughClasses ? 'fa-check' : 'fa-times'"></i> At least 3 of: uppercase, lowercase, digit, symbol ({{classCount}}/4 so far)</li>
      <li *ngIf="confirmPassword !== undefined && confirmPassword !== null && confirmPassword !== '' && !passwordsMatch">
        <i class="fa fa-times"></i> Passwords don't match yet
      </li>
    </ul>
  `,
  styles: [`
    .password-requirements-checklist {
      list-style: none;
      padding: 0;
      margin: 0 0 0 4px;
      text-align: left;
      font-size: 12px;
    }
    .password-requirements-checklist li {
      color: #a94442;
    }
    .password-requirements-checklist li.met {
      color: #3c763d;
    }
    .password-requirements-checklist li i {
      width: 14px;
      display: inline-block;
    }
  `]
})
export class PasswordRequirementsChecklistComponent {

  @Input() password: string;
  @Input() confirmPassword: string;

  get hasMinLength(): boolean {
    return (this.password || "").length >= 8;
  }

  get hasUpper(): boolean {
    return /[A-Z]/.test(this.password || "");
  }

  get hasLower(): boolean {
    return /[a-z]/.test(this.password || "");
  }

  get hasDigit(): boolean {
    return /[0-9]/.test(this.password || "");
  }

  get hasSymbol(): boolean {
    return /[^A-Za-z0-9]/.test(this.password || "");
  }

  get classCount(): number {
    return [this.hasUpper, this.hasLower, this.hasDigit, this.hasSymbol].filter(met => met).length;
  }

  get hasEnoughClasses(): boolean {
    return this.classCount >= 3;
  }

  get passwordsMatch(): boolean {
    return !!this.password && this.password === this.confirmPassword;
  }

  /** True once every check this component can verify client-side passes. Does NOT cover the
   * blocklist or reuse-history rules - a form should still handle a server rejection even
   * when this is true. */
  get allKnownRequirementsMet(): boolean {
    const confirmProvided = this.confirmPassword !== undefined && this.confirmPassword !== null && this.confirmPassword !== "";
    return this.hasMinLength && this.hasEnoughClasses && (!confirmProvided || this.passwordsMatch);
  }
}
